import { Suspense, useMemo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import { useAuth } from "../hooks/useAuth";
import LoginPage from "../pages/LoginPage";
import NotFoundPage from "../pages/NotFoundPage";
import RegisterPage from "../pages/RegisterPage";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import { resolveMenuComponent } from "./componentRegistry";
import ProtectedRoute from "./ProtectedRoute";
import PublicRoute from "./PublicRoute";
import { generateRoutesFromMenu } from "../utils/menuRouting";
import DashboardPage from "../pages/DashboardPage";
import UsersModulePage from "../pages/users/UsersModulePage";
import RolesModulePage from "../pages/roles/RolesModulePage";
import MenuListPage from "../pages/menus/MenuListPage";
import CountryModulePage from "../pages/country/CountryModulePage";
import PaymentModeModulePage from "../pages/paymentMode/PaymentModeModulePage";
import BankModulePage from "../pages/bank/BankModulePage";
import VendorModulePage from "../pages/vendor/VendorModulePage";
import BLListPage from "../pages/bl/BLListPage";
import BLFormPage from "../pages/bl/BLFormPage";
import BLContainerModulePage from "../pages/bl/BLContainerModulePage";
import StockCategoryModulePage from "../pages/stockCategory/StockCategoryModulePage";
import CurrencyUomModulePage from "../pages/currencyUom/CurrencyUomModulePage";
import LotModulePage from "../pages/lot/LotModulePage";
import YardModulePage from "../pages/yard/YardModulePage";
import ContainerWiseReportPage from "../pages/reports/ContainerWiseReportPage";
import BLWiseReportPage from "../pages/reports/BLWiseReportPage";
import PurchaseMasterWiseReportPage from "../pages/reports/PurchaseMasterWiseReportPage";
import StockReportPage from "../pages/reports/StockReportPage";
import BillReportPage from "../pages/reports/BillReportPage";
import SellingReportPage from "../pages/reports/SellingReportPage";
import LotWiseReportPage from "../pages/reports/LotWiseReportPage";
import PnLReportPage from "../pages/reports/PnLReportPage";
import APAgingReportPage from "../pages/reports/APAgingReportPage";
import ARAgingReportPage from "../pages/reports/ARAgingReportPage";
import CustomerReportPage from "../pages/reports/CustomerReportPage";
import MISReportsHubPage from "../pages/reports/MISReportsHubPage";
import PurchaseMasterListPage from "../pages/purchaseMaster/PurchaseMasterListPage";
import PurchaseMasterFormPage from "../pages/purchaseMaster/PurchaseMasterFormPage";
import ShippingLineModulePage from "../pages/shippingLine/ShippingLineModulePage";
import CustomerListPage from "../pages/customer/CustomerListPage";
import CustomerFormPage from "../pages/customer/CustomerFormPage";
import SellingMasterListPage from "../pages/sellingMaster/SellingMasterListPage";
import SellingMasterFormPage from "../pages/sellingMaster/SellingMasterFormPage";
import ExpenseCategoryModulePage from "../pages/expenseCategory/ExpenseCategoryModulePage";
import ExpenseOverheadListPage from "../pages/expenseOverhead/ExpenseOverheadListPage";
import ExpenseOverheadFormPage from "../pages/expenseOverhead/ExpenseOverheadFormPage";
import CompanyModulePage from "../pages/company/CompanyModulePage";
import PurchasePaymentMasterPage from "../pages/purchasePayment/PurchasePaymentMasterPage";
import PurchasePaymentDetailPage from "../pages/purchasePayment/PurchasePaymentDetailPage";
import SalesPaymentMasterPage from "../pages/salesPayment/SalesPaymentMasterPage";
import SalesPaymentDetailPage from "../pages/salesPayment/SalesPaymentDetailPage";
import StockProcessingPage from "../pages/bl/StockProcessingPage";
import BulkStockProcessingCreatePage from "../pages/bl/BulkStockProcessingCreatePage";
import StockTransferListPage from "../pages/bl/StockTransferListPage";
import StockTransferCreatePage from "../pages/bl/StockTransferCreatePage";
import BLExpensePage from "../pages/bl/BLExpensePage";

function RouteFallback() {
  return (
    <div className="flex min-h-[200px] items-center justify-center text-slate-700 dark:text-slate-200">
      Loading page...
    </div>
  );
}

function AppRoutes() {
  const { myMenus, isAuthenticated, isMenusLoading } = useAuth();

  const dynamicRoutes = useMemo(
    () => generateRoutesFromMenu(myMenus, resolveMenuComponent),
    [myMenus],
  );


  const renderDynamicRoute = (routeNode) => {
    const PageComponent = routeNode.component;
    const currentRoute = (
      <Route
        key={routeNode.id}
        path={routeNode.path}
        element={
          <ProtectedRoute menu={routeNode.menu} action="read">
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <PageComponent />
              </Suspense>
            </AppShell>
          </ProtectedRoute>
        }
      />
    );
    const childRoutes = (routeNode.children || []).flatMap((child) =>
      renderDynamicRoute(child),
    );
    return [currentRoute, ...childRoutes];
  };

  if (isAuthenticated && isMenusLoading) {
    return <RouteFallback />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Navigate
            to={isAuthenticated ? "/dashboard" : "/login"}
            replace
          />
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppShell>
              <DashboardPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <AppShell>
              <UsersModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <UsersModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <UsersModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/roles"
        element={
          <ProtectedRoute>
            <AppShell>
              <RolesModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <RolesModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <RolesModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/menus"
        element={
          <ProtectedRoute>
            <AppShell>
              <MenuListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Country Routes */}
      <Route
        path="/country"
        element={
          <ProtectedRoute>
            <AppShell>
              <CountryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/country/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <CountryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/country/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <CountryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master/country"
        element={
          <ProtectedRoute>
            <AppShell>
              <CountryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/country"
        element={
          <ProtectedRoute>
            <AppShell>
              <CountryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Payment Mode Routes */}
      <Route
        path="/payment-mode"
        element={
          <ProtectedRoute>
            <AppShell>
              <PaymentModeModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-mode/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <PaymentModeModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-mode/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <PaymentModeModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master/payment-mode"
        element={
          <ProtectedRoute>
            <AppShell>
              <PaymentModeModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/payment-mode"
        element={
          <ProtectedRoute>
            <AppShell>
              <PaymentModeModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Bank Master Routes */}
      <Route
        path="/bank"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master/bank"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/bank"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/bank/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/bank/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank-master/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank-master/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/bank-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/bank-master/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/bank-master/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <BankModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Vendor Routes */}
      <Route
        path="/vendors"
        element={
          <ProtectedRoute>
            <AppShell>
              <VendorModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <VendorModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <VendorModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master/vendors"
        element={
          <ProtectedRoute>
            <AppShell>
              <VendorModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/vendors"
        element={
          <ProtectedRoute>
            <AppShell>
              <VendorModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* BL Master Routes */}
      <Route
        path="/bl-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bl-master/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bl-master/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bl-management"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bl-management/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bl-management/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bl-management/stock-processing"
        element={
          <ProtectedRoute>
            <AppShell>
              <StockProcessingPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bl-management/stock-processing/bulk-stock-processing"
        element={
          <ProtectedRoute>
            <AppShell>
              <BulkStockProcessingCreatePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfer-stock"
        element={
          <ProtectedRoute>
            <AppShell>
              <StockTransferListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfer-stock/create"
        element={
          <ProtectedRoute>
            <AppShell>
              <StockTransferCreatePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bl-master/:id/expenses"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLExpensePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock-processing/bulk-stock-processing"
        element={
          <ProtectedRoute>
            <AppShell>
              <BulkStockProcessingCreatePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* BL Container Routes */}
      <Route
        path="/bl-container-stock-details"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLContainerModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bl-container-stock-details/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLContainerModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bl-container-stock-details/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLContainerModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Stock Category Routes */}
      <Route
        path="/stock-category"
        element={
          <ProtectedRoute>
            <AppShell>
              <StockCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock-category/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <StockCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock-category/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <StockCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/stock-category"
        element={
          <ProtectedRoute>
            <AppShell>
              <StockCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Currency UOM Routes */}
      <Route
        path="/currency-uom"
        element={
          <ProtectedRoute>
            <AppShell>
              <CurrencyUomModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/currency-uom/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <CurrencyUomModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/currency-uom/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <CurrencyUomModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/currency-uom"
        element={
          <ProtectedRoute>
            <AppShell>
              <CurrencyUomModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Lot Routes */}
      <Route
        path="/lot"
        element={
          <ProtectedRoute>
            <AppShell>
              <LotModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lot/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <LotModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lot/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <LotModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/lot"
        element={
          <ProtectedRoute>
            <AppShell>
              <LotModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Yard Routes */}
      <Route
        path="/yard"
        element={
          <ProtectedRoute>
            <AppShell>
              <YardModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/yard/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <YardModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/yard/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <YardModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/yard"
        element={
          <ProtectedRoute>
            <AppShell>
              <YardModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* User Master Routes */}
      <Route
        path="/user-master/user"
        element={
          <ProtectedRoute>
            <AppShell>
              <UsersModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-master/roles"
        element={
          <ProtectedRoute>
            <AppShell>
              <RolesModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-master/menus"
        element={
          <ProtectedRoute>
            <AppShell>
              <MenuListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Report Routes */}
      <Route
        path="/reports/container-wise"
        element={
          <ProtectedRoute>
            <AppShell>
              <ContainerWiseReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/container-wise/:blContainerId"
        element={
          <ProtectedRoute>
            <AppShell>
              <ContainerWiseReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/bl-wise"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLWiseReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/bl-wise/:blId"
        element={
          <ProtectedRoute>
            <AppShell>
              <BLWiseReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/purchase-master-wise"
        element={
          <ProtectedRoute>
            <AppShell>
              <PurchaseMasterWiseReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/purchase-master-wise/:purchaseMasterId"
        element={
          <ProtectedRoute>
            <AppShell>
              <PurchaseMasterWiseReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/stock-report"
        element={
          <ProtectedRoute>
            <AppShell>
              <StockReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/stock-report/:stockCategoryId"
        element={
          <ProtectedRoute>
            <AppShell>
              <StockReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/bill-report"
        element={
          <ProtectedRoute>
            <AppShell>
              <BillReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/selling-report"
        element={
          <ProtectedRoute>
            <AppShell>
              <SellingReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/lot-wise"
        element={
          <ProtectedRoute>
            <AppShell>
              <LotWiseReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/lot-wise/:lotId"
        element={
          <ProtectedRoute>
            <AppShell>
              <LotWiseReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/pnl-report"
        element={
          <ProtectedRoute>
            <AppShell>
              <PnLReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ap-aging"
        element={
          <ProtectedRoute>
            <AppShell>
              <APAgingReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ar-aging"
        element={
          <ProtectedRoute>
            <AppShell>
              <ARAgingReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/customer-report"
        element={
          <ProtectedRoute>
            <AppShell>
              <CustomerReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/customer-report/:customerId"
        element={
          <ProtectedRoute>
            <AppShell>
              <CustomerReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* MIS Reports Hub Route */}
      <Route
        path="/mis-reports"
        element={
          <ProtectedRoute>
            <AppShell>
              <MISReportsHubPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/msi-reports"
        element={<Navigate to="/mis-reports" replace />}
      />

      {/* Purchase Master Routes */}
      <Route
        path="/purchase-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <PurchaseMasterListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-master/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <PurchaseMasterFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-master/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <PurchaseMasterFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-master/:id/view"
        element={
          <ProtectedRoute>
            <AppShell>
              <PurchaseMasterFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Purchase Payment Master Routes */}
      <Route
        path="/purchase-payment-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <PurchasePaymentMasterPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bl-management/purchase-payment-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <PurchasePaymentMasterPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-payment-master/:id/payments"
        element={
          <ProtectedRoute>
            <AppShell>
              <PurchasePaymentDetailPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Sales Payment Master Routes */}
      <Route
        path="/sales-payment-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <SalesPaymentMasterPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoice-management/sales-payment-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <SalesPaymentMasterPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-payment-master/:id/payments"
        element={
          <ProtectedRoute>
            <AppShell>
              <SalesPaymentDetailPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Shipping Line Routes */}
      <Route
        path="/shipping-line"
        element={
          <ProtectedRoute>
            <AppShell>
              <ShippingLineModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipping-line/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <ShippingLineModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipping-line/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <ShippingLineModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/shipping-line"
        element={
          <ProtectedRoute>
            <AppShell>
              <ShippingLineModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Customer Master Routes */}
      <Route
        path="/customer-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <CustomerListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-master/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <CustomerFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-master/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <CustomerFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/customer-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <CustomerListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/customer-master/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <CustomerFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/customer-master/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <CustomerFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Selling Master Routes */}
      <Route
        path="/selling-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <SellingMasterListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/selling-master/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <SellingMasterFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/selling-master/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <SellingMasterFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/selling-master/:id/view"
        element={
          <ProtectedRoute>
            <AppShell>
              <SellingMasterFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoice-management/selling-master"
        element={
          <ProtectedRoute>
            <AppShell>
              <SellingMasterListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoice-management/selling-master/:id/view"
        element={
          <ProtectedRoute>
            <AppShell>
              <SellingMasterFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Expense Category Routes */}
      <Route
        path="/expense-category"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expense-category/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expense-category/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expense-categories"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expense-categories/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expense-categories/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/expense-category"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/expense-categories"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseCategoryModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Expense Overhead Routes */}
      <Route
        path="/expense-overhead"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseOverheadListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expense-overhead/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseOverheadFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expense-overhead/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseOverheadFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/expense-overhead"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseOverheadListPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/expense-overhead/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseOverheadFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/expense-overhead/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <ExpenseOverheadFormPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Company Routes */}
      <Route
        path="/company"
        element={
          <ProtectedRoute>
            <AppShell>
              <CompanyModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/company/new"
        element={
          <ProtectedRoute>
            <AppShell>
              <CompanyModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/company/:id/edit"
        element={
          <ProtectedRoute>
            <AppShell>
              <CompanyModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings-master/company"
        element={
          <ProtectedRoute>
            <AppShell>
              <CompanyModulePage />
            </AppShell>
          </ProtectedRoute>
        }
      />



      {dynamicRoutes.flatMap((routeNode) => renderDynamicRoute(routeNode))}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default AppRoutes;
